import { Collapse, Grid } from '@mui/material';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SHOP } from '../../config/fe-routes';
import useUser from '../../hooks/useUser';
import ShopFeedbackService from '../../services/shop-feedback.service';
import formatDate from '../../utils/format-date';
import ImageUrlResizer from '../../utils/image-url-resizer';
import { useNotification } from '../../utils/snackbar';
import A from '../Link/Link';
import ProfileImage from '../ProfileImage/ProfileImage';
import Button from '../ui-kit/Button/Button';
import Modal from '../ui-kit/Modal/Modal';
import Textarea from '../ui-kit/Textarea/Textarea';
import { ShopFeedbackActionType, ShopFeedbackProps } from './ShopFeedback';

type ShopFeedbackReplyProps = {
  feedback: ShopFeedbackProps['feedback'];
  feedbackReceiver: ShopFeedbackProps['feedbackReceiver'];
  onRefresh: ShopFeedbackProps['onRefresh'];
  onClickSender?: ShopFeedbackProps['onClickSender'];
};

const shopFeedbackService = new ShopFeedbackService();

export const ShopFeedbackReply = ({ feedback, feedbackReceiver, onClickSender, onRefresh }: ShopFeedbackReplyProps) => {
  const { t } = useTranslation();
  const { showSuccessNotification } = useNotification();
  const { user } = useUser();

  const [reply, setReply] = useState(feedback?.reply ?? '');
  const [editReply, setEditReply] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setSubmitting] = useState(false);
  const [isReplyExpanded, setIsReplyExpanded] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

  // Determine if reply text is long enough to need "show more"
  const MAX_REVIEW_LENGTH = 750;
  const isReplyLong = feedback?.reply && feedback.reply.length > MAX_REVIEW_LENGTH;

  useEffect(() => {
    // Reset expanded state when feedback reply changes
    setIsReplyExpanded(false);
  }, [feedback?.reply]);

  const onEditReply = (edit: boolean) => {
    setError(null);
    setEditReply(edit);
    setReply(feedback?.reply ?? '');
    // Reset expanded state when editing
    setIsReplyExpanded(false);
  };

  const onCancelReply = () => {
    setError(null);
    setEditReply(false);
    setReply(feedback?.reply ?? '');
  };

  const onDeleteReply = async () => {
    setSubmitting(true);

    try {
      const response = await shopFeedbackService.deleteShopFeedbackReply(feedback?.id);

      await onRefresh?.(response, ShopFeedbackActionType.REPLY_DELETED);

      setEditReply(false);
      showSuccessNotification(t('components.feedback.reply.delete-success'));
    } catch (_) {
      // Do nothing.
    } finally {
      setSubmitting(false);
    }
  };

  const onSaveReply = async () => {
    setError(null);
    setSubmitting(true);

    try {
      const response = await shopFeedbackService.addShopFeedbackReply(feedback?.id, {
        reply,
      });

      await onRefresh?.(response, ShopFeedbackActionType.REPLY_ADDED_OR_UPDATED);

      setEditReply(false);
      showSuccessNotification(t('components.feedback.success'));
    } catch (error) {
      if (error?.data?.data?.errorCode === 'FEEDBACK_REPLY_EDIT_LIMIT_REACHED') {
        setError(t('components.feedback.reply.errors.edit-limit'));
      } else if (error?.data?.data?.errorCode === 'VALIDATION_ERROR' && !!error?.data?.data?.errors?.reply) {
        setError(t('components.feedback.reply.errors.missing-rating'));
      } else {
        setError(error?.data?.data?.msg || t('common.general-error'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Reply. */}
      {editReply ? (
        <div className={classNames('shop-feedback-reply shop-feedback-reply--edit', { 'has-feedback': true })}>
          {/* Header: avatar + name/date in a row */}
          <div className="shop-feedback-reply__edit-header">
            <A className="shop-feedback-link no-style" {...SHOP(feedbackReceiver?.shop?.activeSlug)} onClick={() => onClickSender?.()}>
              <ProfileImage src={ImageUrlResizer.resize(feedbackReceiver?.picture, 300)} />
            </A>
            <div className="shop-feedback-name">
              <A className="shop-feedback-link no-style" {...SHOP(feedbackReceiver?.shop?.activeSlug)} onClick={() => onClickSender?.()}>
                {feedbackReceiver?.shop?.name}
              </A>
              <div className="shop-feedback-date">{formatDate(feedback?.repliedAt ?? new Date().toISOString(), true)}</div>
            </div>
          </div>

          {/* Textarea full-width below the header */}
          <Textarea
            value={reply}
            onChange={(value) => {
              setReply(value);
              setError(null);
            }}
            placeholder={t('components.feedback.reply.placeholder')}
            maxLength={150}
            minRows={3}
            autoFocus={true}
            disabled={isSubmitting}
          />

          {!!error && <div className="reply-error">{error}</div>}

          {feedback?.canReply && (
            <Grid container className="reply-actions" flexWrap="nowrap" gap={1}>
              <Grid item>
                <Button buttonType="outlined" size="small" onClick={onCancelReply} disabled={isSubmitting}>
                  {t('components.feedback.cancel-reply')}
                </Button>
              </Grid>
              <Grid item>
                <Button buttonType="primaryOutlined" size="small" onClick={onSaveReply} disabled={isSubmitting}>
                  {t('components.feedback.save-reply')}
                </Button>
              </Grid>
            </Grid>
          )}
        </div>
      ) : (
        <>
          {!feedback?.reply && feedback?.canReply && (
            <Grid container className="reply-actions" flexWrap="nowrap" gap={1}>
              <Grid item>
                <Button buttonType="outlined" size="small" onClick={() => onEditReply(true)}>
                  {t('components.feedback.reply.reply')}
                </Button>
              </Grid>
            </Grid>
          )}
          {!!feedback?.reply && (
            <div className={classNames('shop-feedback-reply', { 'has-feedback': !!feedback?.reply })}>
              {!!feedback?.reply && (
                <div className="shop-feedback-reply__header">
                  <A
                    className="shop-feedback-link no-style"
                    {...SHOP(feedbackReceiver?.shop?.activeSlug)}
                    onClick={() => onClickSender?.()}
                  >
                    <ProfileImage src={ImageUrlResizer.resize(feedbackReceiver?.picture, 300)} />
                  </A>
                  <div className="shop-feedback-name">
                    <A
                      className="shop-feedback-link no-style"
                      {...SHOP(feedbackReceiver?.shop?.activeSlug)}
                      onClick={() => onClickSender?.()}
                    >
                      {feedbackReceiver?.shop?.name}
                    </A>
                    <div className="shop-feedback-date">{formatDate(feedback.repliedAt, true)}</div>
                  </div>
                </div>
              )}

              <div className="shop-feedback">
                {feedback?.reply && (
                  <div className="shop-feedback-text">
                    {isReplyLong ? (
                      <>
                        <div style={{ display: isReplyExpanded ? 'none' : 'block' }}>
                          {feedback.reply.substring(0, MAX_REVIEW_LENGTH)}...
                        </div>
                        <Collapse in={isReplyExpanded}>
                          <div>{feedback.reply}</div>
                        </Collapse>
                        <Button
                          buttonType="outlined"
                          size="small"
                          onClick={() => setIsReplyExpanded(!isReplyExpanded)}
                          sx={{ mt: 0.5, textTransform: 'none', minWidth: 'auto', padding: 0 }}
                        >
                          {isReplyExpanded ? t('common.show-less') : t('common.show-more')}
                        </Button>
                      </>
                    ) : (
                      <div>{feedback.reply}</div>
                    )}
                  </div>
                )}

                {/* Reply actions for receiver. */}
                {user?.shop?.id === feedbackReceiver?.shop?.id && (
                  <Grid container className={classNames('reply-actions', { 'has-reply': !!feedback?.reply })} flexWrap="nowrap" gap={1}>
                    {!editReply && (
                      <>
                        {/* Replies can only be edited once; the API says when the edit is used up. */}
                        {feedback?.canReply && (!feedback?.reply || feedback?.canEditReply !== false) && (
                          <Grid item>
                            <Button buttonType="outlined" size="small" onClick={() => onEditReply(true)} disabled={isSubmitting}>
                              {!feedback?.reply ? t('components.feedback.reply.reply') : t('components.feedback.edit-reply')}
                            </Button>
                          </Grid>
                        )}

                        {!!feedback?.reply && (
                          <Grid item>
                            <Button buttonType="danger" size="small" onClick={() => setDeleteModalOpen(true)} disabled={isSubmitting}>
                              {t('components.feedback.reply.delete-reply')}
                            </Button>
                          </Grid>
                        )}
                      </>
                    )}

                    {editReply && (
                      <>
                        <Grid item>
                          <Button buttonType="outlined" size="small" onClick={onCancelReply} disabled={isSubmitting}>
                            {t('components.feedback.cancel-reply')}
                          </Button>
                        </Grid>
                        <Grid item>
                          <Button buttonType="primaryOutlined" size="small" onClick={onSaveReply} disabled={isSubmitting}>
                            {t('components.feedback.save-reply')}
                          </Button>
                        </Grid>
                      </>
                    )}
                  </Grid>
                )}
              </div>
            </div>
          )}
        </>
      )}

      <Modal
        open={isDeleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title={t('components.feedback.reply.title')}
        className="shop-feedback-delete-modal"
        actions={
          <>
            <Button buttonType="outlined" onClick={() => setDeleteModalOpen(false)}>
              {t('common.back')}
            </Button>
            <Button
              buttonType="primary"
              disabled={isSubmitting}
              onClick={async () => {
                await onDeleteReply();
                setDeleteModalOpen(false);
              }}
            >
              {t('components.feedback.delete-confirm.yes-delete')}
            </Button>
          </>
        }
      >
        {t('components.feedback.reply.message')}
      </Modal>
    </>
  );
};
